import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

const TeacherCodeModal = ({ passport, form, onClose }) => {
	const API_URL = import.meta.env.VITE_API_URL;
	const queryClient = useQueryClient();
	const [password, setPassword] = useState();
	const [success, setSuccess] = useState(false);
	const [isDone, setIsDone] = useState(false);

	const { data : passportResultData } = useQuery({
		queryKey: ['passportResult', passport?.id, form.explore.code, form.explore.level],
		queryFn: async () => {
			const params = new URLSearchParams();
			params.append("passportId", passport?.id);
			params.append("exploreCode", form.explore.code);
			params.append("exploreLevel", form.explore.level);

			const response = await fetch(`${API_URL}/bbs/ajax.get_passport_result.php`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/x-www-form-urlencoded',
				},
				body: params,
				credentials: 'include'
			});
			
			if (!response.ok) throw new Error("네트워크 오류");

			return response.json();
		},
		enabled: !!passport?.id,
	});

	const passportResultItems = passportResultData?.data?.list ?? null;

	const mutation = useMutation({
		mutationFn: async (data: { passportId: string, exploreCode: string, exploreLevel: string, type: string, id: string, password: string }) => {
			const params = new URLSearchParams();
			Object.entries(data).forEach(([key, value]) => {
				params.append(key, value);
			});

			const response = await fetch(`${API_URL}/bbs/ajax.update_passport.php`, {
				method: "POST",
				headers: {
					"Content-Type": "application/x-www-form-urlencoded",
				},
				body: params,
			});
			
			if (!response.ok) throw new Error("네트워크 오류");

			return response.json();
		},
		onSuccess: (response) => {
			if(!response.success) {
				switch (response.code) {
					case "INVALID_REQUEST":
						alert("필수 필드값이 입력되지 않았습니다.");
						break;
					case "NOT_MATCH_PASSPORT_PASSWORD":
						alert("PIN 번호가 일치하지 않습니다.");
						break;
					default :
						alert("알 수 없는 오류가 발생했습니다.");
						break;
				}
				return;
			}

			if(!response.success) {
				alert("알 수 없는 오류가 발생했습니다.");
				return;
			}

			if(response.data) {
				setSuccess(true);
				setIsDone(response.data.isDone);
				queryClient.invalidateQueries(['passport']); 
			}
		},
		onError: (error: any) => {
			alert(error.message);
		}
	});

	const handleChange = (e) => {
		let value = e.target.value;
		value = value.replace(/[^0-9]/g, '');
		if (value.length > 4) return;
		setPassword(value);
	};

	const handleClick = () => {
		if (!/^\d{4}$/.test(password)) {
			alert("비밀번호는 숫자 4자리여야 합니다.");
			return;
		}
		mutation.mutate({passportId: passport?.id, exploreCode: form.explore.code, exploreLevel: form.explore.level, type: form.type, id: form.id, password: password});
	};

	const badges = [
        {id: 'speed', icon:'🏃', name:'스피드러너', desc:'한 달 안에 한 나라 완주'},
        {id: 'perfect', icon:'🎯', name:'완벽주의자', desc:'모든 프로젝트 100점'},
        {id: 'presenter', icon:'🎤', name:'발표왕', desc:'프로젝트 10번 발표'},
        {id: 'passion', icon:'🔥', name:'열정왕', desc:'3일 연속 미션 완수'}
    ];

	return (
		<div className="fixed inset-0 bg-black/70 z-[1000] flex items-center justify-center p-5 backdrop-blur-sm">
			<div className="bg-white rounded-[20px] p-10 max-w-[500px] w-full shadow-[0_20px_60px_rgba(0,0,0,0.3)]">
				{success && form?.type == "mission" && isDone && passportResultItems?.filter(v => v.type === 'mission' && v.isDone)?.length >= 5 && (
					<>
						<div className="text-6xl mb-4">🏆</div>
						<h3 className="text-2xl md:text-3xl font-bold mb-4">나라 완주!</h3>
						<p className="text-lg md:text-xl mb-6 text-gray-600">{form.exploreName}를 완전히 정복했어요!</p>
						<button
							onClick={onClose}
							className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-xl font-bold transition-colors"
						>
							확인
						</button>
					</>
				)}
				{success && form?.type == "mission" && isDone && passportResultItems?.filter(v => v.type === 'mission' && v.isDone)?.length < 5 && (
					<>
						<div className="text-6xl mb-4">⭐</div>
						<h3 className="text-2xl md:text-3xl font-bold mb-4">스탬프 획득!</h3>
						<p className="text-lg md:text-xl mb-6 text-gray-600">미션을 완료했어요!</p>
						<button
							onClick={onClose}
							className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-xl font-bold transition-colors"
						>
							확인
						</button>
					</>
				)}
				{success && form?.type == "badge" && !isDone && (
					<>
						<div className="text-6xl mb-4">🔒</div>
						<h3 className="text-2xl md:text-3xl font-bold mb-4">배지 회수</h3>
						<p className="text-lg md:text-xl mb-6 text-gray-600">배지가 제거되었습니다.</p>
						<button
							onClick={onClose}
							className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-xl font-bold transition-colors"
						>
							확인
						</button>
					</>
				)}
				{success && form?.type == "badge" && isDone && (
					<>
						<div className="text-6xl mb-4">{badges?.find(b => b.id === form?.id)?.icon}</div>
						<h3 className="text-2xl md:text-3xl font-bold mb-4">{badges?.find(b => b.id === form?.id)?.name} 배지 획득!</h3>
						<p className="text-lg md:text-xl mb-6 text-gray-600">{form.exploreName}에서 {badges?.find(b => b.id === form?.id)?.desc}</p>
						<button
							onClick={onClose}
							className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-xl font-bold transition-colors"
						>
							확인
						</button>
					</>
				)}
				{!success && (
					<>
						<h3 className="text-2xl font-bold mb-4 text-center">선생님 인증</h3>
						<p className="text-center mb-6 text-gray-600">
							스탬프 인증을 위한 PIN을 입력하세요
						</p>
						<input 
							type="password"
							name="password"
							id="password"
							onChange={handleChange}
							value={password}
							maxLength="4" 
							className="w-full px-4 py-3 text-center text-2xl font-bold border-2 border-blue-300 rounded-xl mb-4 focus:outline-none focus:border-blue-500 transition-colors" 
							placeholder="• • • •"
						/>
						<div className="flex gap-4">
							<button 
								onClick={onClose}
								className="flex-1 bg-gray-200 px-6 py-3 rounded-xl font-bold hover:bg-gray-300 transition-colors"
							>
								취소
							</button>
							<button 
								onClick={handleClick}
								className="flex-1 bg-blue-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-blue-700 transition-colors"
							>
								확인
							</button>
						</div>
						{passport?.changeDatetime == "0000-00-00 00:00:00" && (
							<p className="text-xs text-center mt-4 text-gray-500">초기 비밀번호는 보호자 휴대전화번호 뒷자리 4자리입니다.</p>
						)}
					</>
				)}
			</div>
		</div>
	);
}

export default TeacherCodeModal;